import { observer } from "mobx-react";
import React, { useCallback, useMemo } from "react";
import { useAppSettings, useAuth } from "Shared/Hooks";
import { useGlobalSetting } from "Store/GlobalSettingStore";

import { useAccount } from "@azure/msal-react";
import { AvatarProps, Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Persona } from "@fluentui/react-components";
import { MoreHorizontalRegular, PersonRegular } from "@fluentui/react-icons";

import { useStyles } from "./UserDropDown.styles";

interface UserType {
    homeAccountId: string;
    tenantId: string;
    username: string;
}

const UserDropDownCachedUserBase = ({ user }: { user: UserType }) => {
    const classNames = useStyles();
    const { microsoftTenantId, torusTenantId } = useAppSettings();
    const { loginWithUser, logoutAccount } = useAuth();
    const account = useAccount(user);
    const { removeUser } = useGlobalSetting("auth");

    const reLoginUser = useCallback(() => {
        loginWithUser(user.username);
    }, [loginWithUser, user.username]);

    const getTenantName = useCallback(
        ({ tenantId, username }: UserType) => {
            switch (tenantId) {
                case microsoftTenantId:
                    return "Microsoft";
                case torusTenantId:
                    return "Torus";
                default:
                    return getDomainFromUser(username);
            }
        },
        [microsoftTenantId, torusTenantId]
    );

    const buttonClick = useCallback(
        () => (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
        },
        []
    );

    const cachedUserAvator: AvatarProps = useMemo(
        () => ({
            icon: <PersonRegular />,
            size: 32
        }),
        []
    );

    const signOut = useCallback(() => {
        if (account) {
            logoutAccount(account);
        }
    }, [account, logoutAccount]);

    const signOutAndForget = useCallback(() => {
        if (account) {
            removeUser(account.username);
            logoutAccount(account);
        }
    }, [account, logoutAccount, removeUser]);

    const forget = useCallback(() => removeUser(user.username), [removeUser, user.username]);

    const items = useMemo(
        () =>
            account ? (
                <MenuList>
                    <MenuItem onClick={signOut} aria-label="sign out">
                        Sign out
                    </MenuItem>
                    <MenuItem onClick={signOutAndForget} aria-label="sign out and forget">
                        Sign out and forget
                    </MenuItem>
                </MenuList>
            ) : (
                <MenuList>
                    <MenuItem aria-label="forget" onClick={forget}>
                        Forget
                    </MenuItem>
                </MenuList>
            ),
        [account, forget, signOut, signOutAndForget]
    );

    return (
        <MenuItem key={user.homeAccountId} className={classNames.cachedItem}>
            <Persona
                className={classNames.cachedPersonal}
                avatar={cachedUserAvator}
                primaryText={getTenantName(user)}
                secondaryText={user.username}
                onClick={reLoginUser}
            />
            <Menu openOnHover>
                <MenuTrigger>
                    <Button className={classNames.cachedButton} icon={<MoreHorizontalRegular />} appearance="subtle" onClick={buttonClick} aria-label="more" />
                </MenuTrigger>
                <MenuPopover>{items}</MenuPopover>
            </Menu>
        </MenuItem>
    );
};

export const UserDropDownCachedUser = observer(UserDropDownCachedUserBase);

function getDomainFromUser(username: string) {
    const userArrays = username.split("@");
    const domainFullName = userArrays.length > 1 ? userArrays[1] : undefined;
    return domainFullName?.split(".")[0];
}
